// Moon phase calculation utilities

/**
 * Calculate moon phase for a given date using astronomical calculations
 * @param {Date} date - The date to calculate moon phase for
 * @returns {Object} Object containing emoji, name, and illumination percentage
 */
export const getMoonPhase = (date) => {
  /**
   * Convert a date to Julian date number
   * Julian dates provide a continuous count of days since noon on January 1, 4713 BCE
   * This is used in astronomical calculations for consistency across calendar systems
   */
  const getJulianDate = (date) => {
    // Algorithm from "Astronomical Algorithms" by Jean Meeus
    const a = Math.floor((14 - (date.getMonth() + 1)) / 12);
    const y = date.getFullYear() + 4800 - a;
    const m = (date.getMonth() + 1) + 12 * a - 3;
    
    // Julian day calculation formula
    return date.getDate() + Math.floor((153 * m + 2) / 5) + 365 * y + 
           Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
  };
  
  // Known new moon date (January 6, 2000, 18:14 UTC)
  // This serves as our reference point for calculations
  const knownNewMoon = new Date('2000-01-06T18:14:00Z');
  
  // Average lunar cycle in days (synodic month)
  // This is the time from new moon to new moon
  const synodicMonth = 29.53058867;
  
  // Convert both dates to Julian date numbers for calculation
  const julianDate = getJulianDate(date);
  const julianKnownNewMoon = getJulianDate(knownNewMoon);
  
  // Calculate days since the known new moon
  const daysSinceNewMoon = julianDate - julianKnownNewMoon;
  
  // Get phase as percentage of lunar cycle (0-1)
  // 0 = new moon, 0.25 = first quarter, 0.5 = full moon, 0.75 = last quarter
  const phase = (daysSinceNewMoon % synodicMonth) / synodicMonth;
  const normalizedPhase = phase < 0 ? phase + 1 : phase;
  
  // Calculate illumination percentage (0-100)
  // Uses cosine function to model the moon's illumination throughout its cycle
  const illumination = Math.round((1 - Math.cos(normalizedPhase * 2 * Math.PI)) * 50);
  
  // Determine phase name and emoji with more precision
  const getPhaseDetails = (phase) => {
    // 16 phase divisions for more accuracy
    if (phase < 0.0185) return { emoji: '🌑', name: 'New Moon', illumination: 0 };
    if (phase < 0.0555) return { emoji: '🌒', name: 'Waxing Crescent (1%)', illumination };
    if (phase < 0.0925) return { emoji: '🌒', name: 'Waxing Crescent (5%)', illumination };
    if (phase < 0.1295) return { emoji: '🌒', name: 'Waxing Crescent (10%)', illumination };
    if (phase < 0.1665) return { emoji: '🌒', name: 'Waxing Crescent (20%)', illumination };
    if (phase < 0.2035) return { emoji: '🌒', name: 'Waxing Crescent (30%)', illumination };
    if (phase < 0.2405) return { emoji: '🌓', name: 'First Quarter (40%)', illumination };
    if (phase < 0.2775) return { emoji: '🌓', name: 'First Quarter', illumination };
    if (phase < 0.3145) return { emoji: '🌔', name: 'Waxing Gibbous (60%)', illumination };
    if (phase < 0.3515) return { emoji: '🌔', name: 'Waxing Gibbous (70%)', illumination };
    if (phase < 0.3885) return { emoji: '🌔', name: 'Waxing Gibbous (80%)', illumination };
    if (phase < 0.4255) return { emoji: '🌔', name: 'Waxing Gibbous (90%)', illumination };
    if (phase < 0.4815) return { emoji: '🌕', name: 'Full Moon', illumination: 100 };
    if (phase < 0.5185) return { emoji: '🌕', name: 'Full Moon', illumination: 100 };
    if (phase < 0.5555) return { emoji: '🌖', name: 'Waning Gibbous (90%)', illumination };
    if (phase < 0.5925) return { emoji: '🌖', name: 'Waning Gibbous (80%)', illumination };
    if (phase < 0.6295) return { emoji: '🌖', name: 'Waning Gibbous (70%)', illumination };
    if (phase < 0.6665) return { emoji: '🌖', name: 'Waning Gibbous (60%)', illumination };
    if (phase < 0.7035) return { emoji: '🌗', name: 'Last Quarter (50%)', illumination };
    if (phase < 0.7405) return { emoji: '🌗', name: 'Last Quarter', illumination };
    if (phase < 0.7775) return { emoji: '🌘', name: 'Waning Crescent (30%)', illumination };
    if (phase < 0.8145) return { emoji: '🌘', name: 'Waning Crescent (20%)', illumination };
    if (phase < 0.8515) return { emoji: '🌘', name: 'Waning Crescent (10%)', illumination };
    if (phase < 0.8885) return { emoji: '🌘', name: 'Waning Crescent (5%)', illumination };
    if (phase < 0.9255) return { emoji: '🌘', name: 'Waning Crescent (1%)', illumination };
    return { emoji: '🌑', name: 'New Moon', illumination: 0 };
  };
  
  return getPhaseDetails(normalizedPhase);
};

/**
 * Calculate approximate moonrise and moonset times
 * Note: This is a simplified calculation - actual times vary by location, date, and lunar orbit
 * For Cape San Blas, this provides reasonable approximations
 * @param {Date} date - The date to calculate moon times for
 * @param {number} latitude - Location latitude (default: Cape San Blas)
 * @returns {Object} Object containing formatted moonrise, moonset times and phase
 */
export const getMoonTimes = (date, latitude = 29.6705) => {
  // Get current moon phase
  const phase = getMoonPhase(date);
  
  // Moon timing is related to its phase:
  // New moon: rises/sets with the sun
  // First quarter: rises at noon, sets at midnight
  // Full moon: rises at sunset, sets at sunrise
  // Last quarter: rises at midnight, sets at noon
  const phaseOffset = (phase.illumination / 100) * 12; // hours offset from sun
  
  // Approximate sunrise at 6:30 AM for Cape San Blas (simplified)
  const baseSunrise = new Date(date);
  baseSunrise.setHours(6, 30, 0, 0);
  
  // Calculate moonrise based on phase offset
  const moonrise = new Date(baseSunrise);
  moonrise.setHours(baseSunrise.getHours() + phaseOffset);
  
  // Moon is visible for approximately 12.4 hours (average)
  const moonset = new Date(moonrise);
  moonset.setHours(moonrise.getHours() + 12.4);
  
  // Format times
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };
  
  return {
    moonrise: formatTime(moonrise),
    moonset: formatTime(moonset),
    phase: phase
  };
};